import React, { useState } from 'react';
import { Grid, Button, TextField } from '@mui/material';
import { useQueryClient } from 'react-query';
import { schedulerService } from '../../../../core/services/schedulerService';
import { GeneralSettings } from '../../../../types/scheduler';
import styles from '../SchedulerSetting.module.scss';

interface EditGeneralSettingsProps {
	settings: GeneralSettings;
	handleBackClick?: () => void;
	onSettingsUpdated?: () => void;
}

const EditGeneralSettings: React.FC<EditGeneralSettingsProps> = ({
	settings,
	handleBackClick,
	onSettingsUpdated,
}) => {
	const queryClient = useQueryClient();
	const [settingsBuffer, setSettingsBuffer] = useState(() => {
		// Get workplace from localStorage if not provided in settings
		const getWorkplace = () => {
			if (settings.workplace) {
				return settings.workplace;
			}
			const workplaceId = localStorage.getItem("workplace-id");
			return workplaceId ? `/api/workplaces/${workplaceId}` : null;
		};

		return {
			workplace: getWorkplace(),
			ageOfMinor: settings.ageOfMinor || 18,
			overtimeDailyHoursAt: settings.overtimeDailyHoursAt || 8,
			overtimeDailyWarningAt: settings.overtimeDailyWarningAt || 7,
			overtimeWeeklyHoursAt: settings.overtimeWeeklyHoursAt || 40,
			timeIncrement: settings.timeIncrement || 15,
			overtimeWeeklyWarningAt: settings.overtimeWeeklyWarningAt || 36,
			defaultShiftLengthHours: settings.defaultShiftLengthHours || 8,
			minShiftLength: settings.minShiftLength || 2,
			maxShiftLength: settings.maxShiftLength || 16,
		};
	});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const validateSettings = (): boolean => {
		// Validate age of minor
		if (settingsBuffer.ageOfMinor < 0 || settingsBuffer.ageOfMinor > 120) {
			setError('Age of minor must be between 0 and 120');
			return false;
		}

		// Validate overtime daily hours
		if (settingsBuffer.overtimeDailyHoursAt <= 0 || settingsBuffer.overtimeDailyHoursAt > 24) {
			setError('Overtime daily hours must be between 1 and 24');
			return false;
		}

		// Validate overtime daily warning
		if (
			settingsBuffer.overtimeDailyWarningAt < 0 ||
			settingsBuffer.overtimeDailyWarningAt >= settingsBuffer.overtimeDailyHoursAt
		) {
			setError('Overtime daily warning must be less than overtime daily hours');
			return false;
		}

		// Validate overtime weekly hours
		if (settingsBuffer.overtimeWeeklyHoursAt <= 0 || settingsBuffer.overtimeWeeklyHoursAt > 168) {
			setError('Overtime weekly hours must be between 1 and 168');
			return false;
		}

		// Validate overtime weekly warning
		if (
			settingsBuffer.overtimeWeeklyWarningAt < 0 ||
			settingsBuffer.overtimeWeeklyWarningAt >= settingsBuffer.overtimeWeeklyHoursAt
		) {
			setError('Overtime weekly warning must be less than overtime weekly hours');
			return false;
		}

		// Validate time increment
		if (settingsBuffer.timeIncrement <= 0 || settingsBuffer.timeIncrement > 60) {
			setError('Time increment must be between 1 and 60 minutes');
			return false;
		}

		// Validate default shift length
		if (settingsBuffer.defaultShiftLengthHours <= 0 || settingsBuffer.defaultShiftLengthHours > 24) {
			setError('Default shift length must be between 1 and 24 hours');
			return false;
		}

		// Validate min shift length
		if (settingsBuffer.minShiftLength <= 0 || settingsBuffer.minShiftLength > 24) {
			setError('Minimum shift length must be between 1 and 24 hours');
			return false;
		}

		// Validate max shift length
		if (settingsBuffer.maxShiftLength <= 0 || settingsBuffer.maxShiftLength > 24) {
			setError('Maximum shift length must be between 1 and 24 hours');
			return false;
		}

		// Validate min < max
		if (settingsBuffer.minShiftLength >= settingsBuffer.maxShiftLength) {
			setError('Minimum shift length must be less than maximum shift length');
			return false;
		}

		// Validate max consecutive work days
		// if (settingsBuffer.maxConsecutiveWorkDays < 1 || settingsBuffer.maxConsecutiveWorkDays > 14) {
		// 	setError('Max consecutive work days must be between 1 and 14');
		// 	return false;
		// }

		return true;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);

		if (!validateSettings()) {
			return;
		}

        try {
            setIsSubmitting(true);
            await schedulerService.saveSettings({
                workplace: settingsBuffer.workplace,
                ageOfMinor: settingsBuffer.ageOfMinor,
                overtimeDailyHoursAt: settingsBuffer.overtimeDailyHoursAt,
                overtimeDailyWarningAt: settingsBuffer.overtimeDailyWarningAt,
                overtimeWeeklyHoursAt: settingsBuffer.overtimeWeeklyHoursAt,
                timeIncrement: settingsBuffer.timeIncrement,
                overtimeWeeklyWarningAt: settingsBuffer.overtimeWeeklyWarningAt,
                defaultShiftLengthHours: settingsBuffer.defaultShiftLengthHours,
                minShiftLength: settingsBuffer.minShiftLength,
                maxShiftLength: settingsBuffer.maxShiftLength,
            }, settings);
			
			// Invalidate generalSettings cache to trigger refetch in SchedulePage
			queryClient.invalidateQueries('generalSettings');
			
			onSettingsUpdated?.();
			setTimeout(() => {
				handleBackClick?.();
			}, 500);
		} catch (err: any) {
			setError(err?.message || 'Failed to update settings');
			setIsSubmitting(false);
		}
	};

	return (
		<div className={styles.editGeneralSettings}>
			<Grid container direction="column" spacing={2} pl={2}>
				<Grid container direction="row" justifyContent="space-between">
					<Button className={styles.backButton} onClick={handleBackClick} variant="contained">
						Back
					</Button>
					<Button
						className={styles.submitButton}
						onClick={handleSubmit}
						disabled={isSubmitting}
						variant="contained"
					>
						{isSubmitting ? 'Saving...' : 'Save'}
					</Button>
				</Grid>
				<Grid>
					<h1 className={styles.formTitle}>Edit General Settings</h1>
				</Grid>
			</Grid>

			<form className={styles.formContainer} onSubmit={handleSubmit}>
				{error && <div style={{ color: '#d32f2f', marginBottom: 16 }}>{error}</div>}

				<TextField
					label="Minor if Less Than"
					name="ageOfMinor"
					type="number"
					value={settingsBuffer.ageOfMinor}
					onChange={(e) =>
						setSettingsBuffer({ ...settingsBuffer, ageOfMinor: Number(e.target.value) })
					}
					placeholder="Enter age of minor"
					className={styles.input}
					margin="normal"
					fullWidth
					required
					inputProps={{ min: 0, max: 120 }}
				/>

				<TextField
					label="Overtime Daily Hours At"
					name="overtimeDailyHoursAt"
					type="number"
					value={settingsBuffer.overtimeDailyHoursAt}
					onChange={(e) =>
						setSettingsBuffer({ ...settingsBuffer, overtimeDailyHoursAt: Number(e.target.value) })
					}
					placeholder="Enter hours"
					className={styles.input}
					margin="normal"
					fullWidth
					required
					inputProps={{ min: 1, max: 24 }}
				/>

				<TextField
					label="Overtime Daily Warning At"
					name="overtimeDailyWarningAt"
					type="number"
					value={settingsBuffer.overtimeDailyWarningAt}
					onChange={(e) =>
						setSettingsBuffer({
							...settingsBuffer,
							overtimeDailyWarningAt: Number(e.target.value),
						})
					}
					placeholder="Enter warning hours"
					className={styles.input}
					margin="normal"
					fullWidth
					required
					inputProps={{ min: 0 }}
				/>

				<TextField
					label="Overtime Weekly Hours At"
					name="overtimeWeeklyHoursAt"
					type="number"
					value={settingsBuffer.overtimeWeeklyHoursAt}
					onChange={(e) =>
						setSettingsBuffer({
							...settingsBuffer,
							overtimeWeeklyHoursAt: Number(e.target.value),
						})
					}
					placeholder="Enter hours"
					className={styles.input}
					margin="normal"
					fullWidth
					required
					inputProps={{ min: 1, max: 168 }}
				/>

				<TextField
					label="Overtime Weekly Warning At"
					name="overtimeWeeklyWarningAt"
					type="number"
					value={settingsBuffer.overtimeWeeklyWarningAt}
					onChange={(e) =>
						setSettingsBuffer({
							...settingsBuffer,
							overtimeWeeklyWarningAt: Number(e.target.value),
						})
					}
					placeholder="Enter warning hours"
					className={styles.input}
					margin="normal"
					fullWidth
					required
					inputProps={{ min: 0 }}
				/>

				<TextField
					label="Time Increment (minutes)"
					name="timeIncrement"
					type="number"
					value={settingsBuffer.timeIncrement}
					onChange={(e) =>
						setSettingsBuffer({ ...settingsBuffer, timeIncrement: Number(e.target.value) })
					}
					placeholder="Enter time increment in minutes"
					className={styles.input}
					margin="normal"
					fullWidth
					required
					inputProps={{ min: 1, max: 60 }}
				/>

				<TextField
					label="Default Shift Length (hours)"
					name="defaultShiftLengthHours"
					type="number"
					value={settingsBuffer.defaultShiftLengthHours}
					onChange={(e) =>
						setSettingsBuffer({ ...settingsBuffer, defaultShiftLengthHours: Number(e.target.value) })
					}
					placeholder="Enter default shift length"
					className={styles.input}
					margin="normal"
					fullWidth
					required
					inputProps={{ min: 1, max: 24 }}
				/>

				<TextField
					label="Minimum Shift Length (hours)"
					name="minShiftLength"
					type="number"
					value={settingsBuffer.minShiftLength}
					onChange={(e) =>
						setSettingsBuffer({ ...settingsBuffer, minShiftLength: Number(e.target.value) })
					}
					placeholder="Enter minimum shift length"
					className={styles.input}
					margin="normal"
					fullWidth
					required
					inputProps={{ min: 1, max: 24 }}
				/>

				<TextField
					label="Maximum Shift Length (hours)"
					name="maxShiftLength"
					type="number"
					value={settingsBuffer.maxShiftLength}
					onChange={(e) =>
						setSettingsBuffer({ ...settingsBuffer, maxShiftLength: Number(e.target.value) })
					}
					placeholder="Enter maximum shift length"
					className={styles.input}
					margin="normal"
					fullWidth
					required
					inputProps={{ min: 1, max: 24 }}
				/>
			</form>
		</div>
	);
};

export default EditGeneralSettings;
